import { useCallback, useEffect, useRef, useState } from 'react';
import { dataService, getErrorMessage } from '@/api';

const FEES_CHANGED_EVENT = 'lms:fees-changed';
const SUMMARY_POLL_MS = 60_000;

/** Tell every fee widget on the page (popup, dashboard card, admin badges) to reload. */
export function emitFeesChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(FEES_CHANGED_EVENT));
}

export function useFeesChangedListener(callback, enabled = true) {
  const ref = useRef(callback);
  ref.current = callback;
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const handler = () => ref.current();
    window.addEventListener(FEES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(FEES_CHANGED_EVENT, handler);
  }, [enabled]);
}

/** Student / parent: { settings: { enabled, upiId, payeeName }, fees } */
export function useMyFees({ enabled = true } = {}) {
  const [state, setState] = useState({ loading: enabled, settings: null, fees: [], error: '' });

  const load = useCallback(async () => {
    try {
      const data = await dataService.getMyFees({ skipGlobalLoader: true });
      setState({
        loading: false,
        settings: data?.settings || null,
        fees: Array.isArray(data?.fees) ? data.fees : [],
        error: '',
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: getErrorMessage(e, 'Failed to load fees') }));
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);
  useFeesChangedListener(load, enabled);

  return { ...state, refresh: load };
}

/** Super-admin: per-student status map + verify-queue count; polls so the badge stays fresh. */
export function useFeeSummary({ enabled = true, poll = false } = {}) {
  const [summary, setSummary] = useState({});
  const [submittedTotal, setSubmittedTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await dataService.getFeeSummary({ skipGlobalLoader: true, skipDedupe: true });
      setSummary(data?.summary || {});
      setSubmittedTotal(Number(data?.submittedTotal) || 0);
    } catch {
      // Badge data is best-effort; the fees screen surfaces real errors.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    void load();
    if (!poll) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, SUMMARY_POLL_MS);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, poll, load]);
  useFeesChangedListener(load, enabled);

  return { summary, submittedTotal, loaded, refresh: load };
}

/** Super-admin fee settings (UPI ID etc.). */
export function useFeeSettings({ enabled = true } = {}) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(enabled);

  const load = useCallback(async () => {
    try {
      const data = await dataService.getFeeSettings();
      setSettings(data?.settings || null);
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);
  useFeesChangedListener(load, enabled);

  return { settings, loading, refresh: load, setSettings };
}
